import { config } from 'config/config';
import routePaths, {
  isAccountPage,
  isHome,
  isPage,
  isStaticPage,
  staticRoutes,
} from 'config/routes';
import { NextRouter } from 'next/router';
import { ChallengeDetail } from 'src/shared/models';
import { addItemToSessionStorage, getSegments, joinSegments } from 'utils';

const isLocalMode = config.APP.ENV === 'local';
const urlSitePattern = /(?<lang>[A-z]{2})-+(?<country>[A-z]{2})?/i;
const urlConPattern = /con\/(?<lang>[A-z]{2})-+(?<country>[A-z]{2})?/i;
export const utmPattern = /utm_.*/i;
const lastSlashRegex = /\/$/i;

declare global {
  interface Window {
    dataLayer: [any];
  }
}

declare const fbq: any;
interface IEvent {
  event: string;
}

interface IEventPage {
  user: {
    userId: string;
    trafficType: string;
    pharmacyId: string;
  };
  debug: {
    codeVersion: string;
  };
  page: {
    county: string;
    language: string;
  };
}
interface IEventPageView extends IEvent {
  page: string;
  virtualTitle?: string;
  virtualUrl?: string;
  uuidUser?: string;
}

interface IEventPageViewWithTracking extends IEventPageView {
  utm_campaign?: string;
  utm_content?: string;
  utm_medium?: string;
  utm_source?: string;
  utm_term?: string;
}
export interface IVirtualUrl {
  section: string;
  pageType: string;
  detail: string;
}

export interface IEventCustomProps {
  eventAction?: string;
  eventCategory?: string;
  eventDetail?: string;
  eventLabel?: string;
  eventName?: string;
  leadId?: string;
}

type IEventCustom = IEvent & IEventCustomProps;

export const virtualUrlInit: IVirtualUrl = {
  section: undefined,
  pageType: undefined,
  detail: undefined,
};

export const customEventInit: IEventCustom = {
  event: undefined,
  eventLabel: undefined,
  eventName: undefined,
  leadId: undefined,
};

export const dispatchPageEvent = (user?: any) => {
  const isClient = typeof window === 'object';
  if (!isClient) {
    return;
  }

  const routeSegments = getSegments();

  const userId = user?.uuidUser || undefined;
  const trafficType = isLocalMode ? 'internal' : 'external';
  const pharmacyId = user?.salesforce?.sapCustomerId || undefined;

  /**
   * @todo Create versioning scripting related to release tags and updating
   * application info in package.json
   */
  const codeVersion = '0.1.0';

  const [country, language] = isLocalMode
    ? Array(2).fill(undefined)
    : routeSegments[1].split('-');

  const page: IEventPage = {
    user: {
      userId: userId,
      trafficType: trafficType,
      pharmacyId: pharmacyId,
    },
    debug: {
      codeVersion: codeVersion,
    },
    page: {
      county: country,
      language: language,
    },
  };

  if (!window?.dataLayer?.find((data) => data.page === page.page))
    window?.dataLayer?.push(page);

  return page;
};

export const validAnalyticsPage = (page: string) => {
  return !page.includes('static?page=%2') && !page.includes('[id]?id=');
};

export const dispatchPageViewEvent = (
  router: NextRouter,
  virtualUrl?: IVirtualUrl,
  user?: any,
  challenge?: ChallengeDetail,
) => {
  const isClient =
    typeof window === 'object' && typeof window.dataLayer === 'object';

  if (!isClient) return;

  const { pathname, asPath, query } = router;
  const cleanAsPath = cleanUrl(asPath);
  const cleanPathName = cleanUrl(pathname);
  const isUserLogged: boolean = typeof user === 'object' ? user?.idUser : false;
  const isAnyHome: boolean = isHome(cleanPathName);
  const isPrivateHome: boolean = !!(isAnyHome && isUserLogged);
  const isPublicHome: boolean = !!(isAnyHome && !isUserLogged);
  const isAccount: boolean = isAccountPage(cleanPathName);
  const isStatic: boolean = isStaticPage(cleanPathName);

  const routeSegments = window.location.href
    .replace(window.location.origin + '/', '')
    .split('/');

  let urlSite = isLocalMode ? 'local' : routeSegments[1];
  const urlSiteFound: RegExpMatchArray = urlSite.match(urlSitePattern);

  if (!isLocalMode && urlSiteFound?.length > 0) {
    const { groups } = urlSiteFound;
    urlSite = `${groups.lang?.toLowerCase()}${
      groups.country && `-${groups.country.toUpperCase()}`
    }`;
  }

  const { section, pageType, detail } = {
    ...virtualUrlInit,
    ...virtualUrl,
  };

  const urlSection = (() => {
    if (section) return section;
    switch (true) {
      case cleanPathName === routePaths.FOOTER.CONTACT_US:
      case cleanPathName === routePaths.FOOTER.FAQS:
      case cleanPathName === routePaths.FOOTER.PRODUCT_POINTS:
      case isStatic && query?.page === staticRoutes.FOOTER.COOKIES_POLICY:
      case isStatic && query?.page === staticRoutes.FOOTER.PRIVACY_POLICY:
      case isStatic && query?.page === staticRoutes.FOOTER.TERMS:
        return 'footer';
      case isStatic && query?.page === staticRoutes.STATIC_PAGES.PRODUCTS:
        return 'products';
      case isStatic:
        return typeof query.page === 'string'
          ? query?.page.replace('/', '')
          : null;
      case cleanPathName === routePaths.PAGES.EXPERIENCE:
      case cleanPathName === routePaths.PAGES.EXPERIENCE_DETAIL:
        return 'experiences';
      case cleanPathName === routePaths.PAGES.SCAN.SCAN_INDEX:
        return 'qr-scan';
      case isAccount:
        return 'user-area';
      case isPrivateHome:
      case isPublicHome:
      case isPage(cleanPathName):
        return 'entry';
      default:
        return null;
    }
  })();

  const urlPageType = (() => {
    if (pageType) return pageType;
    switch (true) {
      case cleanPathName === routePaths.FOOTER.CONTACT_US:
        return 'contact';
      case cleanPathName === routePaths.FOOTER.FAQS:
        return 'faqs';
      case cleanPathName === routePaths.FOOTER.PRODUCT_POINTS:
        return 'product-points';
      case isStatic && query?.page === staticRoutes.FOOTER.COOKIES_POLICY:
        return 'cookies';
      case isStatic && query?.page === staticRoutes.FOOTER.PRIVACY_POLICY:
        return 'privacy';
      case isStatic && query?.page === staticRoutes.FOOTER.TERMS:
        return 'terms-conditions';
      case cleanPathName === routePaths.PAGES.LOGIN:
      case cleanPathName === routePaths.PAGES.RECOVERY_PASSWORD:
        return 'login';
      case cleanPathName === routePaths.PAGES.SIGNUP:
        return 'register';
      case cleanPathName === routePaths.PAGES.MY_ACCOUNT.MY_ADDRESSES:
        return 'addresses';
      case cleanPathName === routePaths.PAGES.MY_ACCOUNT.MY_PHARMACY:
        return 'pharmacy';
      case cleanPathName === routePaths.PAGES.MY_ACCOUNT.MY_POINTS:
        return 'points';
      case cleanPathName === routePaths.PAGES.MY_ACCOUNT.MY_BONUS:
        return 'bonus';
      case cleanPathName === routePaths.PAGES.MY_ACCOUNT.MY_PROFILE:
        return 'profile';
      case cleanPathName === routePaths.PAGES.EXPERIENCE_DETAIL:
      case cleanPathName === routePaths.PAGES.CHALLENGE_DETAIL:
      case cleanPathName === routePaths.PAGES.FORMATION_DETAIL:
        return challenge?.challengeCode || null;
      case cleanPathName === routePaths.PAGES.SCAN.SCAN_INDEX:
        return 'scan-products';
      case isAnyHome:
        return 'home';
      default:
        return null;
    }
  })();

  const urlDetail = (() => {
    if (detail) return detail;
    switch (true) {
      case isPrivateHome:
        return '';
      case isPublicHome:
        return 'public';
      case cleanPathName === routePaths.PAGES.RECOVERY_PASSWORD:
        return 'restore-password';
      default:
        return null;
    }
  })();

  if (!urlSection && !urlPageType && !urlDetail) return;

  /** @todo Review and apply other countries event name */
  const conPlatformEvent = 'paginavirtual';
  const virtualUrlPattern = [urlSite, urlSection, urlPageType, urlDetail];

  let pageView: IEventPageView = {
    event: conPlatformEvent,
    virtualTitle: document.title,
    virtualUrl: joinSegments(virtualUrlPattern),
    page: cleanAsPath,
  };

  let pageViewWithTracking: IEventPageViewWithTracking = {
    ...pageView,
  };

  const localTraking = getTracking(router.query);

  const tracking = getTrackingFrom(localTraking);

  pageViewWithTracking = {
    ...pageViewWithTracking,
    ...tracking,
  };

  if (checkPageUrlUtms(pageViewWithTracking.page, pageViewWithTracking))
    window?.dataLayer?.push(pageViewWithTracking);

  return pageViewWithTracking;
};

export const dispatchCustomEvent = (dispatchCustomEvent: IEventCustomProps) => {
  const isClient = typeof window === 'object';

  if (!isClient) return;

  const {
    eventAction,
    eventCategory,
    eventDetail,
    eventLabel,
    eventName,
    leadId,
  } = {
    ...customEventInit,
    ...dispatchCustomEvent,
  };

  const custom: IEventCustom = {
    event: 'eventga',
    eventAction: eventAction,
    eventCategory: eventCategory,
    eventDetail: eventDetail,
    eventLabel: eventLabel,
    eventName: eventName,
    leadId: leadId,
  };

  window?.dataLayer?.push(custom);

  return custom;
};

export const dispatchTrackEvent = (name: string): void => {
  if (name && typeof fbq === 'function') fbq('track', name);
};

export const addTrackingToSessionStorage = (query: object): void => {
  if (!query) return;

  const tracking = getTrackingFrom(query);

  if (tracking && Object.values(tracking).length) {
    for (let [key, value] of Object.entries(tracking)) {
      addItemToSessionStorage(key, value);
    }
  }
};

export const hasTracking = (params: object): boolean => {
  const tracking = getTrackingFrom(params);

  return tracking && Object.values(tracking).length !== 0;
};

export const getTracking = (query: any) => {
  let params: object = query;

  if (query.hasOwnProperty('redirect')) {
    const redirectUrl = new URL(window.location.origin + query.redirect);
    const redirectSearchParams = new URLSearchParams(redirectUrl.search);
    const redirectParams = Object.fromEntries(redirectSearchParams.entries());

    params = redirectParams;
  }

  if (hasTracking(params) || params.hasOwnProperty('page')) {
    if (params['page']) {
      const urlSearchParams = new URLSearchParams(window.location.search);
      const trackingParams = Object.fromEntries(urlSearchParams.entries());
      if (hasTracking(trackingParams)) {
        params = getTrackingFrom(trackingParams) || null;
      }
    }
    addTrackingToSessionStorage(params);
  }

  return params;
};

export const getTrackingFrom = (storage: object): object | void => {
  if (!storage) return;

  const filtered = Object.keys(storage).reduce((obj, key) => {
    obj[key] = storage[key];
    return obj;
  }, {});

  return filtered;
};

const checkPageUrlUtms = (pagePath: string, dataToPush: Object) => {
  const containsUtms = pagePath.match(utmPattern);

  const dataContainsUtms = Object.keys(dataToPush).filter((key) =>
    key.match(utmPattern),
  );

  return containsUtms ? containsUtms && dataContainsUtms.length > 0 : true;
};

export const cleanUrl = (url: string) => {
  return url.replace(lastSlashRegex, '') === ''
    ? '/'
    : url.replace(lastSlashRegex, '');
};
